import { useState, useEffect, useCallback, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { useWizard } from '@/components/common/wizard/WizardContext'
import useAdsSelectionStore from '@/store/adsSelectionStore'
import { showNotification } from '@/utils/notifications'
import { getAvailableStages, isAudienceExcluded } from '@/utils/ad-campaigns-helpers'
import { GET_AUDIENCES } from '@/graphql/audience-queries'
import { useAudienceDataWithHypotheses } from '@/hooks/useAudiencesWithHypotheses'

// Note: Recommendations logic moved to adsSelectionStore.js

function useAdsSelection() {
    const { getStepContentData, setStepContentData, registerNextGuard, deregisterNextGuard } = useWizard()
    const { clearSelection, selectedAudience, selectedStage, setSelectedAudience } = useAdsSelectionStore()

    const [selectedAudienceForEditing, setSelectedAudienceForEditing] = useState(null)
    const [selectedStageForEditing, setSelectedStageForEditing] = useState(null)
    const [isEditingMode, setIsEditingMode] = useState(false)

    // Get data from localStorage with reactive updates
    const [salesFunnelData, setSalesFunnelData] = useState(() => {
        return getStepContentData('salesFunnelSetup') || {}
    })

    const [campaignObjective] = useState(() => getStepContentData('campaignObjective').selectedObjective)
    const [pixelIsActive] = useState(() => {
        const campaignObjectiveData = getStepContentData('campaignObjective') || {}
        return !!campaignObjectiveData?.pixelIsValid
    })

    // Get audience data for filtering
    const { data: catalogueAudiencesData } = useQuery(GET_AUDIENCES)
    const audiencesData = useAudienceDataWithHypotheses(catalogueAudiencesData, getStepContentData)

    // Listen for data changes
    useEffect(() => {
        const handleStorageChange = () => {
            const newData = getStepContentData('salesFunnelSetup') || {}
            setSalesFunnelData(newData)
        }

        // Listen for localStorage changes
        window.addEventListener('storage', handleStorageChange)

        // Periodically check for changes (fallback)
        const interval = setInterval(handleStorageChange, 100)

        return () => {
            window.removeEventListener('storage', handleStorageChange)
            clearInterval(interval)
        }
    }, [getStepContentData])

    // Restore editing mode if there's a persisted selection
    useEffect(() => {
        if (selectedAudience && selectedStage) {
            setSelectedAudienceForEditing(selectedAudience)
            setSelectedStageForEditing(selectedStage)
            setIsEditingMode(true)
        }
    }, [selectedAudience, selectedStage])

    const audienceSelections = useMemo(() => {
        const rawSelections = salesFunnelData.audienceSelections || {}
        
        // If we don't have audience data yet, return raw selections
        if (!audiencesData?.Audience?.audiences) {
            return rawSelections
        }
        
        const allAudiences = audiencesData.Audience.audiences
        
        // Filter out excluded audiences based on pixel status
        const filteredSelections = Object.keys(rawSelections).reduce((acc, stageId) => {
            const stageAudiences = rawSelections[stageId] || []
            const validAudiences = stageAudiences.filter(audienceId => {
                const audience = allAudiences.find(a => a.id === audienceId)
                return audience && !isAudienceExcluded(audience, pixelIsActive)
            })
            acc[stageId] = validAudiences
            return acc
        }, {})
        
        return filteredSelections
    }, [salesFunnelData.audienceSelections, audiencesData, pixelIsActive])

    const adCreativeSelections = useMemo(() => {
        return salesFunnelData.adCreativeSelections || {}
    }, [salesFunnelData.adCreativeSelections])

    // Clean up wizard state when audiences are filtered out
    useEffect(() => {
        if (!audiencesData?.Audience?.audiences) return
        
        const rawSelections = salesFunnelData.audienceSelections || {}
        const hasFilteredOutAudiences = JSON.stringify(rawSelections) !== JSON.stringify(audienceSelections)
        
        if (hasFilteredOutAudiences) {
            // Also clean up ad creative selections for removed audiences
            const allAudiences = audiencesData.Audience.audiences
            const cleanedAdCreativeSelections = {}
            
            // Only keep ad creative selections for valid (non-excluded) audiences
            Object.keys(adCreativeSelections).forEach(stageAudienceKey => {
                const [stageId, audienceId] = stageAudienceKey.split('_')
                const audience = allAudiences.find(a => a.id === audienceId)
                
                if (audience && !isAudienceExcluded(audience, pixelIsActive)) {
                    cleanedAdCreativeSelections[stageAudienceKey] = adCreativeSelections[stageAudienceKey]
                }
            })
            
            // Update wizard state with filtered data
            const updatedData = {
                ...salesFunnelData,
                audienceSelections: audienceSelections,
                adCreativeSelections: cleanedAdCreativeSelections
            }
            
            setStepContentData(updatedData, 'salesFunnelSetup')
            setSalesFunnelData(updatedData)
        }
    }, [audienceSelections, audiencesData, pixelIsActive, salesFunnelData, adCreativeSelections, setStepContentData])

    // Handle editing an audience
    const handleEditAudience = useCallback(
        (audience, stage) => {
            setSelectedAudienceForEditing(audience)
            setSelectedStageForEditing(stage)
            setIsEditingMode(true)
            // Persist selection in Zustand store
            setSelectedAudience(audience, stage)
        },
        [setSelectedAudience],
    )

    // Handle removing all creatives for an audience in a specific stage
    const handleRemoveCreative = useCallback(
        (params) => {
            const { audienceId, stageId, creativeId } = params
            const stageAudienceKey = `${stageId}_${audienceId}`
            // Remove all ad creatives for this stage-audience combination
            const updatedData = {
                ...salesFunnelData,
                adCreativeSelections: {
                    ...adCreativeSelections,
                    [stageAudienceKey]: adCreativeSelections[stageAudienceKey].filter((id) => id !== creativeId),
                },
            }

            setStepContentData(updatedData, 'salesFunnelSetup')
            setSalesFunnelData(updatedData) // Update local state immediately
        },
        [adCreativeSelections, salesFunnelData, setStepContentData],
    )

    // Handle creative selection toggle
    const handleCreativeSelect = useCallback(
        (creativeId) => {
            if (!selectedAudienceForEditing || !selectedStageForEditing) return

            const stageAudienceKey = `${selectedStageForEditing.id}_${selectedAudienceForEditing.id}`
            const currentSelections = adCreativeSelections[stageAudienceKey] || []

            let newSelections
            if (currentSelections.includes(creativeId)) {
                // Remove if already selected
                newSelections = currentSelections.filter((id) => id !== creativeId)
            } else {
                // Add if not selected
                newSelections = [...currentSelections, creativeId]
            }

            // Update localStorage immediately
            const updatedData = {
                ...salesFunnelData,
                adCreativeSelections: {
                    ...adCreativeSelections,
                    [stageAudienceKey]: newSelections,
                },
            }

            setStepContentData(updatedData, 'salesFunnelSetup')
            setSalesFunnelData(updatedData) // Update local state immediately
        },
        [
            selectedAudienceForEditing,
            selectedStageForEditing,
            adCreativeSelections,
            salesFunnelData,
            setStepContentData,
        ],
    )

    // Get selected creatives for an audience in a specific stage
    const getSelectedCreativesForAudience = useCallback(
        (audienceId, stageId) => {
            const stageAudienceKey = `${stageId}_${audienceId}`
            return adCreativeSelections[stageAudienceKey] || []
        },
        [adCreativeSelections],
    )

    // Get total creative count for a stage
    const getTotalCreativesForStage = useCallback(
        (stageId) => {
            const audienceIds = audienceSelections[stageId] || []
            return audienceIds.reduce((total, audienceId) => {
                const stageAudienceKey = `${stageId}_${audienceId}`
                return total + (adCreativeSelections[stageAudienceKey]?.length || 0)
            }, 0)
        },
        [audienceSelections, adCreativeSelections],
    )

    // Check if creative is selected for current audience
    const isCreativeSelected = useCallback(
        (creativeId) => {
            if (!selectedAudienceForEditing || !selectedStageForEditing) return false
            const stageAudienceKey = `${selectedStageForEditing.id}_${selectedAudienceForEditing.id}`
            return adCreativeSelections[stageAudienceKey]?.includes(creativeId) || false
        },
        [selectedAudienceForEditing, selectedStageForEditing, adCreativeSelections],
    )

    // Note: Recommendations and warnings logic moved to adsSelectionStore.js for simplicity

    // Validation function for wizard guard
    const validateMinimumCreatives = useCallback(() => {
        // Find all audiences without creatives
        const audiencesWithoutCreatives = []
        const availableStages = getAvailableStages(campaignObjective)

        for (const [stageId, audienceIds] of Object.entries(audienceSelections)) {
            for (const audienceId of audienceIds) {
                const stageAudienceKey = `${stageId}_${audienceId}`
                if (!adCreativeSelections[stageAudienceKey]?.length && availableStages.includes(stageId)) {
                    audiencesWithoutCreatives.push({ stageId, audienceId })
                }
            }
        }

        if (audiencesWithoutCreatives.length > 0) {
            // Show error notification
            showNotification(
                `Please attach at least one creative to each audience. ${audiencesWithoutCreatives.length} audience${audiencesWithoutCreatives.length > 1 ? 's' : ''} missing creatives.`,
                'error',
                5000,
                null,
                null,
                true,
                true,
            )
            return false
        }

        return true
    }, [audienceSelections, adCreativeSelections])

    // Register wizard guard
    useEffect(() => {
        registerNextGuard(validateMinimumCreatives)
        return () => deregisterNextGuard(validateMinimumCreatives)
    }, [registerNextGuard, deregisterNextGuard, validateMinimumCreatives])

    // Close editing mode
    const closeEditingMode = useCallback(() => {
        setIsEditingMode(false)
        setSelectedAudienceForEditing(null)
        setSelectedStageForEditing(null)
        clearSelection() // Clear store selection as well
    }, [clearSelection])

    return {
        // State
        selectedAudienceForEditing,
        selectedStageForEditing,
        isEditingMode,
        audienceSelections,
        adCreativeSelections,

        // Methods
        handleEditAudience,
        handleRemoveCreative,
        handleCreativeSelect,
        getSelectedCreativesForAudience,
        getTotalCreativesForStage,
        isCreativeSelected,
        validateMinimumCreatives,
        closeEditingMode,
    }
}

export default useAdsSelection
